import { useEffect, useState } from "react";
import axios from "axios";
import {
  Eye, Mail, Download, Loader2, Info, Calendar,
  ShoppingBasket, Receipt, Package, Repeat, User, ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatCameraAssegnata } from "../../utils/formatCamera";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatEuro(n) {
  return `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("it-IT")} ore ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return iso;
  }
}

const SECTION_THEMES = {
  violet: {
    icon: ShoppingBasket,
    title: "text-violet-700",
    totalBg: "bg-violet-50",
    totalText: "text-violet-800",
  },
  emerald: {
    icon: Receipt,
    title: "text-emerald-700",
    totalBg: "bg-emerald-50",
    totalText: "text-emerald-800",
  },
  blue: {
    icon: Package,
    title: "text-blue-700",
    totalBg: "bg-blue-50",
    totalText: "text-blue-800",
  },
  amber: {
    icon: Repeat,
    title: "text-amber-700",
    totalBg: "bg-amber-50",
    totalText: "text-amber-800",
  },
};

function ReportSectionTable({
  number,
  title,
  subtitle,
  themeKey,
  columns,
  rows,
  emptyMessage,
  totalLabel,
  total,
}) {
  const theme = SECTION_THEMES[themeKey];
  const Icon = theme.icon;

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <Icon className={`w-4 h-4 shrink-0 ${theme.title}`} />
        <h4 className={`text-sm font-semibold ${theme.title}`}>
          {number}. {title}
          {subtitle && (
            <span className="font-normal text-slate-500"> ({subtitle})</span>
          )}
        </h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/90 hover:bg-slate-50/90 border-b border-slate-200">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={`text-xs font-semibold text-slate-600 h-9 ${col.align === "right" ? "text-right" : ""}`}
              >
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-slate-500 text-sm py-5 text-center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow key={i} className="border-b border-slate-100 last:border-0">
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={`text-sm text-slate-700 py-2.5 ${col.align === "right" ? "text-right tabular-nums" : ""}`}
                  >
                    {row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
          <TableRow className={`${theme.totalBg} hover:bg-transparent border-0`}>
            <TableCell
              colSpan={columns.length - 1}
              className={`text-sm font-semibold py-2.5 ${theme.totalText}`}
            >
              {totalLabel}
            </TableCell>
            <TableCell className={`text-sm font-bold text-right py-2.5 tabular-nums ${theme.totalText}`}>
              {formatEuro(total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export default function PrimaNotaReportTab({
  token,
  meseCorrente,
  formatMese,
  ospitiAttivi,
  reportOspite,
  setReportOspite,
  filtroOspite,
}) {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const ospite = ospitiAttivi.find((o) => o.id === reportOspite);
  const cameraLabel = ospite?.camera
    ? formatCameraAssegnata(ospite.camera) || ospite.camera
    : null;

  useEffect(() => {
    if (!reportOspite || !token) {
      setDati(null);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_URL}/api/prima-nota/report-dati`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ospiteId: reportOspite, mese: meseCorrente },
      })
      .then((res) => setDati(res.data))
      .catch(() => setDati(null))
      .finally(() => setLoading(false));
  }, [reportOspite, meseCorrente, token]);

  useEffect(() => {
    if (filtroOspite && filtroOspite !== "tutti") {
      setReportOspite(filtroOspite);
    }
  }, [filtroOspite, setReportOspite]);

  const fetchReportPdfBlob = async () => {
    const res = await axios.post(
      `${API_URL}/api/prima-nota/report`,
      { ospiteId: reportOspite, mese: meseCorrente, inviaEmail: false },
      { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
    );
    const blob = res.data;
    if (blob.type && !blob.type.includes("pdf") && blob.type.includes("html")) {
      throw new Error("Il server ha restituito HTML invece di PDF");
    }
    return new Blob([blob], { type: "application/pdf" });
  };

  const pdfFilename = () => {
    const nome = ospite?.nome_completo?.replace(/\s+/g, "_") || "ospite";
    return `report_${meseCorrente}_${nome}.pdf`;
  };

  const previewPdf = async () => {
    if (!reportOspite) return toast.error("Seleziona un ospite");
    setGenerating(true);
    try {
      const blob = await fetchReportPdfBlob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Errore generazione PDF");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async () => {
    if (!reportOspite) return toast.error("Seleziona un ospite");
    setGenerating(true);
    try {
      const blob = await fetchReportPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Errore download PDF");
    } finally {
      setGenerating(false);
    }
  };

  const consumiRows = (dati?.consumiExtra || []).map((c) => ({
    voce: c.label || c.tipo || "—",
    quantita: `${c.quantita ?? 0} pz`,
    importo: formatEuro(c.importo),
  }));

  const speseRows = (dati?.speseExtra || []).map((s) => ({
    voce: s.label || s.descrizione || "—",
    importo: formatEuro(s.importo),
  }));

  const ausiliRows = (dati?.ausiliForniture || []).map((a) => ({
    voce: a.label || "—",
    importo: formatEuro(a.importo),
  }));

  const ricorrenzeRows = (dati?.ricorrenze || []).map((r) => ({
    voce: r.descrizione || "—",
    importo: formatEuro(r.importo),
  }));

  const showRicorrenze =
    ricorrenzeRows.length > 0 || Number(dati?.totaleRicorrenze || 0) > 0;

  const ultimo = dati?.ultimoInvio;

  return (
    <Card className="border border-slate-200/80 shadow-lg rounded-2xl bg-white">
      <CardContent className="p-5 md:p-6">
        {/* Intestazione report */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-5 border-b border-slate-200">
          <div className="space-y-3 min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-900">Report Mensile</h2>
            <div className="space-y-2 max-w-md">
              <Select value={reportOspite || ""} onValueChange={setReportOspite}>
                <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Seleziona ospite..." />
                </SelectTrigger>
                <SelectContent>
                  {ospitiAttivi.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.nome_completo}
                      {o.camera ? ` (${formatCameraAssegnata(o.camera) || o.camera})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reportOspite && ospite && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <User className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="font-medium text-slate-800">
                  {ospite.nome_completo}
                  {cameraLabel && (
                    <span className="text-slate-500 font-normal"> ({cameraLabel})</span>
                  )}
                </span>
                <Badge className="bg-emerald-100 text-emerald-800 border-0 hover:bg-emerald-100 text-xs">
                  Ospite attivo
                </Badge>
              </div>
            )}
          </div>
          <div className="text-left sm:text-right shrink-0">
            <p className="text-xs text-slate-500 mb-1">Periodo di riferimento</p>
            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <Calendar className="w-4 h-4 text-slate-500" />
              {formatMese(meseCorrente)}
            </div>
          </div>
        </div>

        {!reportOspite && (
          <p className="text-sm text-slate-500 py-10 text-center">
            Seleziona un ospite per visualizzare il report del mese.
          </p>
        )}

        {reportOspite && loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
          </div>
        )}

        {reportOspite && !loading && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-6">
              <div className="lg:col-span-2 space-y-4">
                <ReportSectionTable
                  number={1}
                  title="Consumi Extra"
                  themeKey="violet"
                  columns={[
                    { key: "voce", label: "Voce" },
                    { key: "quantita", label: "Quantità", align: "right" },
                    { key: "importo", label: "Importo", align: "right" },
                  ]}
                  rows={consumiRows}
                  emptyMessage="Nessun consumo extra nel periodo"
                  totalLabel="Totale Consumi Extra"
                  total={dati?.totaleConsumiExtra}
                />
                <ReportSectionTable
                  number={2}
                  title="Spese Extra"
                  themeKey="emerald"
                  columns={[
                    { key: "voce", label: "Voce" },
                    { key: "importo", label: "Importo", align: "right" },
                  ]}
                  rows={speseRows}
                  emptyMessage="Nessuna spesa extra nel periodo"
                  totalLabel="Totale Spese Extra"
                  total={dati?.totaleSpeseExtra}
                />
                <ReportSectionTable
                  number={3}
                  title="Ausili e Forniture"
                  subtitle="costi fissi"
                  themeKey="blue"
                  columns={[
                    { key: "voce", label: "Voce" },
                    { key: "importo", label: "Importo", align: "right" },
                  ]}
                  rows={ausiliRows}
                  emptyMessage="Nessuna fornitura configurata in magazzino"
                  totalLabel="Totale Ausili e Forniture"
                  total={dati?.totaleAusiliForniture}
                />
                {showRicorrenze && (
                  <ReportSectionTable
                    number={4}
                    title="Ricorrenze"
                    themeKey="amber"
                    columns={[
                      { key: "voce", label: "Voce" },
                      { key: "importo", label: "Importo", align: "right" },
                    ]}
                    rows={ricorrenzeRows}
                    emptyMessage="Nessuna ricorrenza nel periodo"
                    totalLabel="Totale Ricorrenze"
                    total={dati?.totaleRicorrenze}
                  />
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-3">
                    Riepilogo costi del mese
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>Consumi Extra</span>
                      <span className="font-medium text-slate-800 tabular-nums">
                        {formatEuro(dati?.totaleConsumiExtra)}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Spese Extra</span>
                      <span className="font-medium text-slate-800 tabular-nums">
                        {formatEuro(dati?.totaleSpeseExtra)}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Ausili e Forniture</span>
                      <span className="font-medium text-slate-800 tabular-nums">
                        {formatEuro(dati?.totaleAusiliForniture)}
                      </span>
                    </div>
                    {showRicorrenze && (
                      <div className="flex justify-between text-slate-600">
                        <span>Ricorrenze</span>
                        <span className="font-medium text-slate-800 tabular-nums">
                          {formatEuro(dati?.totaleRicorrenze)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between items-baseline">
                    <span className="font-bold text-slate-900">Totale mese</span>
                    <span className="text-xl font-bold text-rose-600 tabular-nums">
                      {formatEuro(dati?.totaleMese)}
                    </span>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50/90 rounded-lg border border-blue-100 text-xs text-blue-900 flex gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Il report include consumi extra, spese extra e i costi fissi degli ausili
                      {showRicorrenze ? " e delle ricorrenze" : ""} del mese selezionato.
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-4 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                      Ultimo invio ai familiari
                    </div>
                    {ultimo ? (
                      <>
                        <p className="text-sm text-slate-600 mt-1.5">{formatDateTime(ultimo.inviatoAt)}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Inviato a: {ultimo.numDestinatari} familiari
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 mt-1.5">Mai inviato ai familiari</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
              <Button
                variant="outline"
                className="flex-1 border-slate-300"
                disabled={generating}
                onClick={previewPdf}
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Anteprima PDF
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50"
                disabled={generating}
                onClick={async () => {
                  setGenerating(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/prima-nota/report`,
                      { ospiteId: reportOspite, mese: meseCorrente, inviaEmail: true },
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    toast.success("Report inviato ai familiari");
                    const r = await axios.get(`${API_URL}/api/prima-nota/report-dati`, {
                      headers: { Authorization: `Bearer ${token}` },
                      params: { ospiteId: reportOspite, mese: meseCorrente },
                    });
                    setDati(r.data);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Errore invio report");
                  } finally {
                    setGenerating(false);
                  }
                }}
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Invia ai familiari
              </Button>
              <Button
                className="flex-1 bg-rose-500 hover:bg-rose-600"
                disabled={generating}
                onClick={downloadPdf}
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Scarica PDF
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
